import "./styles.css";
import {
  type AuditEvent,
  type CasaOutput,
  type CasaBootstrap,
  type FeedbackResult,
  type HealthResult,
  type OutputsResult,
  type PhaseRow,
  type PlayJob,
  type PlayerStatusPayload,
  type TrackRef,
  type VolumesResult,
  getAuditRecent,
  getAuthConfig,
  getBootstrap,
  getCasaSonoraHealth,
  getPlayJob,
  getPlayerState,
  playMoodAsync,
  sendFeedback,
  setCasaSonoraOutputs,
  setCasaSonoraVolume,
  setPlayerAction,
  signInWithGoogleCredential,
  warmupCasaSonora,
} from "./casa-sonora-api";

type PhaseKey = "queue_pick" | "track_lookup" | "applescript_spawn" | "music_confirm" | "playing";
type ViewKey = "ahora" | "bocinas" | "salud";
type Mood = {
  id: string;
  label: string;
  prompt: string;
  color: string;
};

type AppState = {
  view: ViewKey;
  booting: boolean;
  session: "checking" | "active" | "required";
  warmup: "idle" | "running" | "ready" | "soft_failed";
  warmupMessage: string;
  playState: "idle" | "accepting" | "queued" | "playing" | "failed";
  playerState: "playing" | "paused" | "unknown";
  activeMoodId: string | null;
  activeJobId: string | null;
  activePhase: PhaseKey | "idle" | "failed";
  phaseRows: PhaseRow[];
  phaseTimings: Record<string, number>;
  currentTrack: TrackRef | null;
  outputsLabel: string;
  statusMessage: string;
  errorMessage: string;
  lastTapAckMs: number | null;
  lastJob: PlayJob | null;
  feedbackState: "idle" | "saving" | "saved" | "failed";
  feedbackMessage: string;
  googleClientId: string | null;
  authStatus: "idle" | "loading" | "ready" | "validating" | "failed";
  authMessage: string;
  outputDevices: CasaOutput[];
  selectedOutputNames: string[];
  speakerState: "idle" | "loading" | "ready" | "saving" | "soft_failed";
  speakerMessage: string;
  volumeState: "idle" | "saving" | "saved" | "failed";
  volumeMessage: string;
  volumeValue: number;
  volumeMax: number;
  healthState: "idle" | "loading" | "ready" | "warn" | "down";
  healthMessage: string;
  health: HealthResult | null;
  auditState: "idle" | "loading" | "ready" | "failed";
  auditMessage: string;
  auditEvents: AuditEvent[];
};

type GoogleCredentialResponse = {
  credential?: string;
};

type GoogleAccounts = {
  accounts?: {
    id?: {
      initialize: (options: {
        client_id: string;
        callback: (response: GoogleCredentialResponse) => void;
        auto_select?: boolean;
        cancel_on_tap_outside?: boolean;
        context?: "signin" | "signup" | "use";
        hd?: string;
      }) => void;
      renderButton: (
        parent: HTMLElement,
        options: {
          theme?: "outline" | "filled_blue" | "filled_black";
          size?: "large" | "medium" | "small";
          width?: number;
          text?: "signin_with" | "signup_with" | "continue_with" | "signin";
          shape?: "rectangular" | "pill" | "circle" | "square";
          locale?: string;
        },
      ) => void;
      cancel: () => void;
    };
  };
};

const moods: Mood[] = [
  {
    id: "chill-concentrado",
    label: "chill concentrado",
    prompt:
      "Electrónica chill/downtempo ACTUAL para concentrarse trabajando. Instrumental o vocales mínimas. Nada de soul. Prioriza lo más nuevo y popular.",
    color: "#1f8a83",
  },
  {
    id: "house-curioso",
    label: "house curioso",
    prompt:
      "House actual y popular para descubrir música nueva. Energía media, vocales ligeras ok. Nada underground oscuro. Prioriza lanzamientos recientes.",
    color: "#d66d4f",
  },
  {
    id: "upbeat-con-brillo",
    label: "upbeat con brillo",
    prompt:
      "Electrónica enérgica y brillante, actual y popular. Para subir el ánimo. Sin reguetón; pop solo si es crossover electrónico.",
    color: "#c79a32",
  },
  {
    id: "bajar-revoluciones",
    label: "bajar revoluciones",
    prompt: "Downbeat/deep house suave para cerrar el día. Sin soul, poca vocal. Actual, nada de canciones viejitas.",
    color: "#5f6fa8",
  },
  {
    id: "fiesta",
    label: "fiesta",
    prompt:
      "Hits ACTUALES para fiesta con amigos: pop, reguetón y electrónica popular, incluyendo éxitos en español. Solo lo más popular y reciente del momento, nada underground.",
    color: "#b54a7a",
  },
];

const phaseCopy: Record<PhaseKey, string> = {
  queue_pick: "eligiendo",
  track_lookup: "ubicando",
  applescript_spawn: "conectando",
  music_confirm: "confirmando",
  playing: "suena",
};

const state: AppState = {
  view: "ahora",
  booting: true,
  session: "checking",
  warmup: "idle",
  warmupMessage: "preparando ruta",
  playState: "idle",
  playerState: "unknown",
  activeMoodId: null,
  activeJobId: null,
  activePhase: "idle",
  phaseRows: [],
  phaseTimings: {},
  currentTrack: null,
  outputsLabel: "Solo Mac",
  statusMessage: "abriendo",
  errorMessage: "",
  lastTapAckMs: null,
  lastJob: null,
  feedbackState: "idle",
  feedbackMessage: "",
  googleClientId: null,
  authStatus: "idle",
  authMessage: "Preparando acceso",
  outputDevices: [],
  selectedOutputNames: [],
  speakerState: "idle",
  speakerMessage: "Mac-only listo",
  volumeState: "idle",
  volumeMessage: "Guard max 35 activo",
  volumeValue: 35,
  volumeMax: 35,
  healthState: "idle",
  healthMessage: "sin consultar",
  health: null,
  auditState: "idle",
  auditMessage: "sin consultar",
  auditEvents: [],
};

let pollTimer: number | null = null;
let acceptWaitTimers: number[] = [];
let playerRefreshInFlight = false;
let googleScriptPromise: Promise<void> | null = null;

const app = document.querySelector<HTMLDivElement>("#app");
if (!app) {
  throw new Error("Missing #app root.");
}

function render() {
  const track = normalizeTrack(state.currentTrack);
  const transportAction = state.playerState === "playing" ? "pause" : "play";
  const transportLabel = transportButtonLabel(track, transportAction);
  const transportDisabled = transportDisabledAttr(track);
  const feedbackDisabled = feedbackDisabledAttr(track);
  const phaseItems = (Object.keys(phaseCopy) as PhaseKey[]).map((phase) => {
    const status = phaseStatus(phase);
    const timing = phaseTiming(phase);
    return `
      <li class="phase phase--${status}">
        <span class="phase__dot"></span>
        <span class="phase__label">${phaseCopy[phase]}</span>
        ${timing ? `<span class="phase__time">${timing}</span>` : ""}
      </li>
    `;
  });
  const activeMood = moods.find((mood) => mood.id === state.activeMoodId);
  const statusTone = statusToneForView();
  const artworkStyle = track.artworkUrl ? ` style="background-image:url('${escapeAttribute(track.artworkUrl)}')"` : "";

  app.innerHTML = `
    <main class="shell" aria-label="Casa Sonora">
      <section class="topbar" aria-label="Estado">
        <div>
          <p class="eyeline">Casa Sonora</p>
          <h1>${viewTitle(state.view)}</h1>
        </div>
        <div class="topbar__right">
          <nav class="view-tabs" aria-label="Pantallas Casa Sonora">
            ${(["ahora", "bocinas", "salud"] as ViewKey[])
              .map(
                (view) => `
                  <button type="button" data-view="${view}" class="${state.view === view ? "view-tabs__item view-tabs__item--active" : "view-tabs__item"}">
                    ${viewTitle(view)}
                  </button>
                `,
              )
              .join("")}
          </nav>
          <div class="status status--${statusTone}">
            <span></span>
            <strong>${escapeHtml(statusLabel())}</strong>
          </div>
        </div>
      </section>

      ${state.session === "required" ? renderSessionGate() : `
        ${state.view === "ahora" ? renderNowView({ track, transportAction, transportLabel, transportDisabled, feedbackDisabled, artworkStyle, activeMood, phaseItems }) : ""}
        ${state.view === "bocinas" ? renderSpeakersView() : ""}
        ${state.view === "salud" ? renderHealthView() : ""}
      `}
    </main>
  `;

  bindEvents();
  if (state.session === "required") {
    void mountGoogleSignIn();
  }
}

function renderNowView(input: {
  track: ReturnType<typeof normalizeTrack>;
  transportAction: "play" | "pause";
  transportLabel: string;
  transportDisabled: string;
  feedbackDisabled: string;
  artworkStyle: string;
  activeMood?: Mood;
  phaseItems: string[];
}): string {
  const { track, transportAction, transportLabel, transportDisabled, feedbackDisabled, artworkStyle, activeMood, phaseItems } = input;
  return `
    <section class="now-grid">
      <article class="artwork-panel" aria-label="Cancion actual">
        <div class="artwork"${artworkStyle}>
          ${track.artworkUrl ? "" : `<div class="artwork__fallback"><span>${track.initials}</span></div>`}
        </div>
        <div class="trackline">
          <p class="trackline__label">${trackLabel(track)}</p>
          <h2>${escapeHtml(track.name)}</h2>
          <p>${escapeHtml(trackMeta(track))}</p>
        </div>
        <div class="transport" aria-label="Controles de reproduccion">
          <button class="play-button transport-toggle" type="button" data-action="toggle" aria-label="${transportLabel}" title="${transportLabel}" ${transportDisabled}>
            ${transportAction === "pause" ? pauseIcon() : playIcon()}
            <span>${transportLabel}</span>
          </button>
          <button class="icon-button" type="button" data-feedback="favorite" aria-label="Marcar favorito" title="Intenta marcar favorito en Apple Music y Casa Sonora muestra si el bridge lo confirma." ${feedbackDisabled}>
            ${heartIcon()}
          </button>
          <button class="icon-button" type="button" data-feedback="disliked" aria-label="Marcar no me gusta" title="Intenta marcar no me gusta en Apple Music y Casa Sonora muestra si el bridge lo confirma." ${feedbackDisabled}>
            ${thumbDownIcon()}
          </button>
        </div>
        <p class="feedback-hint">Corazon: intenta favorito en Music. Pulgar abajo: intenta no me gusta. La confirmacion aparece abajo.</p>
      </article>

      <article class="decision-panel" aria-label="Decision de musica">
        <div class="route-card">
          <div>
            <p class="label">bocinas</p>
            <strong>${escapeHtml(state.outputsLabel)}</strong>
          </div>
          <div>
            <p class="label">guard</p>
            <strong>max 35</strong>
          </div>
          <div>
            <p class="label">warmup</p>
            <strong>${warmupLabel()}</strong>
          </div>
        </div>

        <div class="moods" aria-label="Moods">
          ${moods
            .map(
              (mood) => `
                <button class="mood ${mood.id === state.activeMoodId ? "mood--active" : ""}" type="button" data-mood="${mood.id}" style="--mood:${mood.color}" ${controlsDisabledAttr()}>
                  <span></span>
                  ${mood.label}
                </button>
              `,
            )
            .join("")}
        </div>

        <ol class="phases" aria-label="Fases del play">
          ${phaseItems.join("")}
        </ol>

        <div class="result-line" role="${state.errorMessage ? "alert" : "status"}">
          ${state.errorMessage ? escapeHtml(state.errorMessage) : resultLine(activeMood)}
        </div>
      </article>
    </section>
  `;
}

function renderSpeakersView(): string {
  const outputs = speakerOutputs();
  const selectedNames = state.selectedOutputNames.length ? state.selectedOutputNames : outputNamesFromDevices(outputs);
  const hasHouseOutputs = outputs.some((output) => isHouseOutput(output) && output.available !== false);
  const guardText = `techo ${state.volumeMax}`;
  return `
    <section class="utility-grid utility-grid--speakers" aria-label="Bocinas">
      <article class="speaker-primary">
        <p class="eyeline">ruta segura</p>
        <h2>Usar solo Mac</h2>
        <p>Casa Sonora puede quedarse en la Mac mientras los HomePods siguen desconectados.</p>
        <button class="play-button speaker-primary__button" type="button" data-speaker-action="mac-only" ${controlsDisabledAttr()}>
          <span>Usar solo Mac</span>
        </button>
        <div class="guard-band">
          <span></span>
          <strong>Guard visible: max ${state.volumeMax}</strong>
        </div>
      </article>

      <article class="utility-panel" aria-label="Volumen">
        <div class="panel-heading">
          <div>
            <p class="label">volumen</p>
            <h2>${state.volumeValue}</h2>
          </div>
          <strong>${guardText}</strong>
        </div>
        <input class="volume-slider" type="range" min="0" max="${state.volumeMax}" value="${state.volumeValue}" data-volume-slider ${controlsDisabledAttr()} aria-label="Volumen Casa Sonora maximo 35">
        <div class="speaker-actions">
          <button class="secondary-button" type="button" data-volume-action="apply" ${controlsDisabledAttr()}>Aplicar volumen</button>
          <button class="secondary-button" type="button" data-speaker-action="refresh" ${controlsDisabledAttr()}>Actualizar</button>
        </div>
        <p class="result-line result-line--compact" role="${state.volumeState === "failed" ? "alert" : "status"}">${escapeHtml(state.volumeMessage)}</p>
      </article>

      <article class="utility-panel utility-panel--wide" aria-label="Salidas AirPlay">
        <div class="panel-heading">
          <div>
            <p class="label">salidas</p>
            <h2>${hasHouseOutputs ? "AirPlay visto" : "sin HomePods"}</h2>
          </div>
          <strong>${escapeHtml(selectedNames.length ? selectedNames.join(", ") : "Solo Mac")}</strong>
        </div>
        <div class="speaker-list">
          ${outputs.length ? outputs.map(renderSpeakerRow).join("") : renderNoSpeakers()}
        </div>
        <p class="result-line result-line--compact" role="${state.speakerState === "soft_failed" ? "alert" : "status"}">${escapeHtml(state.speakerMessage)}</p>
      </article>
    </section>
  `;
}

function renderSpeakerRow(output: CasaOutput): string {
  const name = String(output.name || "").trim();
  const selected = Boolean(output.selected || output.active || state.selectedOutputNames.includes(name));
  const available = output.available !== false;
  const house = isHouseOutput(output);
  const volume = Number.isFinite(output.soundVolume) ? Number(output.soundVolume) : Number.isFinite(output.volume) ? Number(output.volume) : null;
  const rightLabel = selected ? "activa" : available ? "disponible" : "apagada";
  return `
    <button class="speaker-row ${selected ? "speaker-row--selected" : ""}" type="button" data-output-name="${escapeAttribute(name)}" ${!house || !available || !canUseControls() ? "disabled" : ""}>
      <span class="speaker-row__dot"></span>
      <span>
        <strong>${escapeHtml(name || "Salida sin nombre")}</strong>
        <small>${escapeHtml(speakerMeta(output, house, available, volume))}</small>
      </span>
      <em>${rightLabel}</em>
    </button>
  `;
}

function renderNoSpeakers(): string {
  return `
    <div class="empty-state">
      <strong>HomePods desconectados</strong>
      <span>La ruta Mac-only sigue operable. Cuando Fase 0 reconecte bocinas, aparecerán aquí.</span>
    </div>
  `;
}

function renderHealthView(): string {
  const health = healthSummary();
  return `
    <section class="utility-grid utility-grid--health" aria-label="Salud Casa Sonora">
      <article class="health-hero health-hero--${health.tone}">
        <p class="eyeline">estado</p>
        <h2>${escapeHtml(health.title)}</h2>
        <p>${escapeHtml(health.detail)}</p>
        <button class="secondary-button" type="button" data-health-action="refresh" ${controlsDisabledAttr()}>Actualizar salud</button>
      </article>

      <article class="utility-panel">
        <div class="panel-heading">
          <div>
            <p class="label">bridge</p>
            <h2>${escapeHtml(state.health?.mode || health.bridgeLabel)}</h2>
          </div>
          <strong>${escapeHtml(state.health?.updatedAt ? formatShortTime(state.health.updatedAt) : "sin hora")}</strong>
        </div>
        <dl class="health-facts">
          <div><dt>ruta</dt><dd>${escapeHtml(warmupLabel())}</dd></div>
          <div><dt>Music</dt><dd>${escapeHtml(state.health?.appleMusicBridgeConfigured === false ? "local directo" : "bridge 4312")}</dd></div>
          <div><dt>última actividad</dt><dd>${escapeHtml(lastAuditLabel())}</dd></div>
        </dl>
      </article>

      <article class="utility-panel utility-panel--wide">
        <div class="panel-heading">
          <div>
            <p class="label">qué hacer</p>
            <h2>${escapeHtml(health.actionTitle)}</h2>
          </div>
          <strong>${escapeHtml(health.actionTone)}</strong>
        </div>
        <p class="runbook-copy">${escapeHtml(health.action)}</p>
        <div class="audit-list" aria-label="Auditoria reciente redactada">
          ${state.auditEvents.length ? state.auditEvents.slice(0, 6).map(renderAuditEvent).join("") : renderNoAudit()}
        </div>
      </article>
    </section>
  `;
}

function renderAuditEvent(event: AuditEvent): string {
  const label = [event.kind, event.action, event.result || event.event].filter(Boolean).join(" / ") || "evento";
  const when = formatShortTime(String(event.at || event.time || event.timestamp || ""));
  const detail = redactAuditText(String(event.message || event.error || event.phase || event.upstreamPath || event.path || ""));
  return `
    <div class="audit-row">
      <span></span>
      <strong>${escapeHtml(label)}</strong>
      <small>${escapeHtml(when || "sin hora")}</small>
      <em>${escapeHtml(detail || "redactado")}</em>
    </div>
  `;
}

function renderNoAudit(): string {
  return `
    <div class="empty-state">
      <strong>Sin auditoria visible</strong>
      <span>${escapeHtml(state.auditMessage || "No hay eventos recientes para mostrar.")}</span>
    </div>
  `;
}

function renderSessionGate(): string {
  return `
    <section class="session-gate" aria-label="Sesion requerida">
      <article class="session-card">
        <p class="eyeline">sesion requerida</p>
        <h2>Entra para abrir Casa Sonora</h2>
        <p>${escapeHtml(state.errorMessage || "Inicia sesión con Google para abrir AIR Home.")}</p>
        <div class="google-signin-shell">
          <div id="google-signin-target" class="google-signin-target" aria-label="Iniciar sesión con Google"></div>
        </div>
        <p class="auth-note" role="${state.authStatus === "failed" ? "alert" : "status"}">${escapeHtml(state.authMessage)}</p>
      </article>
      <div class="disabled-preview" aria-label="Moods deshabilitados sin sesion">
        ${moods
          .map(
            (mood) => `
              <button class="mood" type="button" disabled style="--mood:${mood.color}">
                <span></span>
                ${mood.label}
              </button>
            `,
          )
          .join("")}
      </div>
    </section>
  `;
}

function bindEvents() {
  app.querySelectorAll<HTMLButtonElement>("[data-view]").forEach((button) => {
    button.addEventListener("click", () => {
      const view = button.dataset.view as ViewKey;
      if (view === "ahora" || view === "bocinas" || view === "salud") {
        void navigateView(view);
      }
    });
  });

  app.querySelectorAll<HTMLButtonElement>("[data-mood]").forEach((button) => {
    button.addEventListener("click", () => {
      const mood = moods.find((item) => item.id === button.dataset.mood);
      if (mood) {
        void startMood(mood);
      }
    });
  });

  app.querySelector<HTMLButtonElement>('[data-action="toggle"]')?.addEventListener("click", () => {
    if (transportDisabledAttr(normalizeTrack(state.currentTrack))) return;
    void controlPlayer(state.playerState === "playing" ? "pause" : "play");
  });
  app.querySelector<HTMLButtonElement>('[data-feedback="favorite"]')?.addEventListener("click", () => {
    void saveFeedback("favorite");
  });
  app.querySelector<HTMLButtonElement>('[data-feedback="disliked"]')?.addEventListener("click", () => {
    void saveFeedback("disliked");
  });
  app.querySelector<HTMLButtonElement>('[data-speaker-action="mac-only"]')?.addEventListener("click", () => {
    void chooseOutputs([]);
  });
  app.querySelector<HTMLButtonElement>('[data-speaker-action="refresh"]')?.addEventListener("click", () => {
    void loadSpeakerSnapshot();
  });
  app.querySelectorAll<HTMLButtonElement>("[data-output-name]").forEach((button) => {
    button.addEventListener("click", () => {
      const name = button.dataset.outputName || "";
      if (name) {
        void chooseOutputs([name]);
      }
    });
  });
  app.querySelector<HTMLInputElement>("[data-volume-slider]")?.addEventListener("input", (event) => {
    const value = clampUiVolume(Number((event.currentTarget as HTMLInputElement).value));
    state.volumeValue = value;
    state.volumeMessage = `Listo para aplicar ${value}`;
    render();
  });
  app.querySelector<HTMLButtonElement>('[data-volume-action="apply"]')?.addEventListener("click", () => {
    void applyVolume();
  });
  app.querySelector<HTMLButtonElement>('[data-health-action="refresh"]')?.addEventListener("click", () => {
    void loadHealthPanel();
  });
}

async function navigateView(view: ViewKey) {
  if (state.view === view) return;
  state.view = view;
  render();
  if (view === "bocinas") {
    await loadSpeakerSnapshot();
  }
  if (view === "salud") {
    await loadHealthPanel();
  }
}

async function boot() {
  Object.assign(state, {
    booting: true,
    session: "checking",
    statusMessage: "abriendo",
    warmupMessage: "preparando ruta",
    errorMessage: "",
  });
  render();
  const bootstrap = await getBootstrap();
  if (bootstrap.ok) {
    state.session = "active";
    applyBootstrap(bootstrap.data);
  } else {
    if (bootstrap.status === 401) {
      await enterSessionRequired(bootstrap.message);
      return;
    }
    state.statusMessage = "bridge no disponible";
    state.errorMessage = bootstrap.message;
  }
  state.booting = false;
  state.warmup = "running";
  state.warmupMessage = "calentando";
  render();

  const warmup = await warmupCasaSonora();
  if (warmup.ok) {
    state.warmup = warmup.data.routeReady || warmup.data.ready ? "ready" : "soft_failed";
    state.warmupMessage = warmup.data.routePhase || warmup.data.phase || (state.warmup === "ready" ? "lista" : "sin ruta");
  } else {
    state.warmup = "soft_failed";
    state.warmupMessage = warmup.message;
  }
  state.statusMessage = state.warmup === "ready" ? "ruta caliente" : "Mac listo";
  render();
}

function applyBootstrap(payload: CasaBootstrap) {
  const playerState = payload.playerState;
  state.playerState = normalizePlayerState(playerState?.playerState);
  state.currentTrack = payload.currentTrack || playerState?.currentTrack || state.currentTrack;
  state.outputsLabel = outputLabel(payload);
  applyOutputSnapshot(payload);
  const track = normalizeTrack(state.currentTrack);
  state.statusMessage = track.trackId && state.playerState === "playing" ? "sonando" : "listo para decidir";
}

async function startMood(mood: Mood) {
  if (!canUseControls()) return;
  const tappedAt = performance.now();
  stopPolling();
  stopAcceptWait();
  Object.assign(state, {
    activeMoodId: mood.id,
    playState: "accepting",
    activeJobId: null,
    activePhase: "queue_pick",
    phaseRows: [{ phase: "queue_pick", status: "running", at: new Date().toISOString() }],
    phaseTimings: {},
    errorMessage: "",
    statusMessage: "tap recibido",
    lastTapAckMs: null,
    feedbackState: "idle",
    feedbackMessage: "",
  });
  render();
  requestAnimationFrame(() => {
    state.lastTapAckMs = Math.round(performance.now() - tappedAt);
    render();
  });
  startAcceptWait();

  const accepted = await playMoodAsync(mood.prompt);
  stopAcceptWait();
  if (!accepted.ok) {
    if (accepted.status === 401) {
      await enterSessionRequired(accepted.message);
      return;
    }
    state.playState = "failed";
    state.activePhase = "failed";
    state.statusMessage = "fallo";
    state.errorMessage = friendlyError(accepted.message);
    render();
    return;
  }

  state.playState = "queued";
  state.activeJobId = accepted.data.jobId;
  state.activePhase = normalizePhase(accepted.data.phase);
  state.statusMessage = "conectando";
  render();
  pollJob(accepted.data.jobId);
}

function pollJob(jobId: string) {
  stopPolling();
  const tick = async () => {
    const job = await getPlayJob(jobId);
    if (!job.ok) {
      if (job.status === 401) {
        await enterSessionRequired(job.message);
        stopPolling();
        return;
      }
      state.playState = "failed";
      state.activePhase = "failed";
      state.errorMessage = friendlyError(job.message);
      state.statusMessage = "fallo";
      render();
      stopPolling();
      return;
    }

    const finishedPlaying = job.data.state === "playing";
    applyJob(job.data);
    render();

    if (job.data.state === "playing" || job.data.state === "failed") {
      stopPolling();
      if (finishedPlaying) {
        void refreshPlayerState("job_complete");
      }
      return;
    }
  };

  void tick();
  pollTimer = window.setInterval(tick, 450);
}

function applyJob(job: PlayJob) {
  state.lastJob = job;
  state.phaseRows = Array.isArray(job.phases) ? job.phases : [];
  state.phaseTimings = job.result?.phaseTimings || job.phaseTimings || {};
  state.activePhase = normalizePhase(job.phase);

  if (job.state === "failed") {
    const error = friendlyError(typeof job.error === "string" ? job.error : job.error?.message || "Play falló.");
    const failedPhase = typeof job.error === "object" && job.error?.phase ? ` (${job.error.phase})` : "";
    state.playState = "failed";
    state.statusMessage = "fallo";
    state.errorMessage = `${error}${failedPhase}`;
    return;
  }

  if (job.state === "playing") {
    const track = job.result?.playedTrack || job.result?.playerState?.currentTrack || job.result?.currentTrack || null;
    state.currentTrack = track || state.currentTrack;
    state.playState = "playing";
    state.playerState = "playing";
    state.activePhase = "playing";
    state.statusMessage = "suena";
    state.errorMessage = "";
    return;
  }

  state.statusMessage = phaseCopy[state.activePhase as PhaseKey] || "conectando";
}

async function controlPlayer(action: "play" | "pause") {
  if (!canUseControls()) return;
  if (action === "play" && !normalizeTrack(state.currentTrack).trackId) {
    state.statusMessage = "elige un mood";
    state.errorMessage = "";
    render();
    return;
  }
  state.statusMessage = action === "pause" ? "pausando" : "reanudando";
  state.errorMessage = "";
  render();
  const result = await setPlayerAction(action);
  if (!result.ok) {
    if (result.status === 401) {
      await enterSessionRequired(result.message);
      return;
    }
    state.errorMessage = friendlyError(result.message);
    state.statusMessage = "fallo";
  } else {
    applyPlayerSnapshot(result.data);
    state.playerState = action === "pause" ? "paused" : "playing";
    state.statusMessage = action === "pause" ? "pausado" : "sonando";
    void refreshPlayerState("control");
  }
  render();
}

async function saveFeedback(kind: "favorite" | "disliked") {
  if (!canUseControls()) return;
  const track = normalizeTrack(state.currentTrack);
  if (!track.trackId) {
    state.errorMessage = "Aun no hay track para guardar feedback.";
    render();
    return;
  }

  state.feedbackState = "saving";
  state.feedbackMessage = "";
  render();
  const result = await sendFeedback({
    trackId: track.trackId,
    sourcePlaylists: track.sourcePlaylists,
    favorite: kind === "favorite" ? true : undefined,
    disliked: kind === "disliked" ? true : undefined,
  });
  if (!result.ok && result.status === 401) {
    await enterSessionRequired(result.message);
    return;
  }
  state.feedbackState = result.ok ? "saved" : "failed";
  state.feedbackMessage = result.ok ? feedbackResultMessage(kind, result.data) : result.message;
  state.errorMessage = result.ok ? "" : result.message;
  state.statusMessage = result.ok ? "guardado" : "fallo";
  render();
}

async function enterSessionRequired(message: string) {
  stopPolling();
  Object.assign(state, {
    booting: false,
    session: "required",
    warmup: "idle",
    warmupMessage: "esperando sesion",
    playState: "idle",
    activeMoodId: null,
    activeJobId: null,
    activePhase: "idle",
    phaseRows: [],
    phaseTimings: {},
    errorMessage: message || "Inicia sesión con Google para abrir AIR Home.",
    statusMessage: "sesion requerida",
    feedbackState: "idle",
    feedbackMessage: "",
    googleClientId: null,
    authStatus: "loading",
    authMessage: "Preparando Google Sign-In",
  });

  const auth = await getAuthConfig(getCurrentReturnPath());
  if (auth.ok && auth.data.googleClientId) {
    state.googleClientId = auth.data.googleClientId;
    state.authStatus = "loading";
    state.authMessage = "Cargando Google Sign-In";
  } else {
    state.googleClientId = null;
    state.authStatus = "failed";
    state.authMessage = auth.ok ? "Falta configurar Google Sign-In." : auth.message;
  }
  render();
}

async function mountGoogleSignIn() {
  if (state.session !== "required" || !state.googleClientId) return;
  const target = document.querySelector<HTMLDivElement>("#google-signin-target");
  if (!target || target.dataset.mounted === "true") return;

  try {
    await loadGoogleScript();
    const google = (window as typeof window & { google?: GoogleAccounts }).google;
    if (!google?.accounts?.id) {
      throw new Error("Google Sign-In no está disponible.");
    }

    target.dataset.mounted = "true";
    target.innerHTML = "";
    google.accounts.id.initialize({
      client_id: state.googleClientId,
      callback: (response) => {
        void handleGoogleCredential(response?.credential || "");
      },
      auto_select: false,
      cancel_on_tap_outside: true,
      context: "signin",
    });
    google.accounts.id.renderButton(target, {
      theme: "outline",
      size: "large",
      width: Math.min(360, Math.max(260, target.clientWidth || 320)),
      text: "signin_with",
      shape: "pill",
      locale: "es",
    });
    state.authStatus = "ready";
    state.authMessage = "Elige tu cuenta de Google.";
    updateAuthNote();
  } catch (error) {
    state.authStatus = "failed";
    state.authMessage = error instanceof Error ? error.message : "No pude cargar Google Sign-In.";
    updateAuthNote();
  }
}

function loadGoogleScript(): Promise<void> {
  if (googleScriptPromise) return googleScriptPromise;

  googleScriptPromise = new Promise((resolve, reject) => {
    const google = (window as typeof window & { google?: GoogleAccounts }).google;
    if (google?.accounts?.id) {
      resolve();
      return;
    }

    const existing = document.querySelector<HTMLScriptElement>('script[data-google-gsi="1"]');
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("No fue posible cargar Google Sign-In.")), {
        once: true,
      });
      return;
    }

    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.dataset.googleGsi = "1";
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("No fue posible cargar Google Sign-In."));
    document.head.appendChild(script);
  });

  return googleScriptPromise;
}

async function handleGoogleCredential(credential: string) {
  if (!credential) {
    state.authStatus = "failed";
    state.authMessage = "Google no devolvió credencial.";
    updateAuthNote();
    return;
  }

  state.authStatus = "validating";
  state.authMessage = "Validando cuenta y abriendo Casa Sonora";
  updateAuthNote();

  const result = await signInWithGoogleCredential(credential);
  if (!result.ok || !result.data.authenticated) {
    state.authStatus = "failed";
    state.authMessage = result.ok ? "No pude abrir la sesión." : result.message;
    updateAuthNote();
    return;
  }

  state.session = "checking";
  state.statusMessage = "abriendo";
  state.errorMessage = "";
  state.authStatus = "idle";
  state.authMessage = "";
  render();
  await boot();
}

function updateAuthNote() {
  const note = document.querySelector<HTMLElement>(".auth-note");
  if (!note) return;
  note.textContent = state.authMessage;
  note.setAttribute("role", state.authStatus === "failed" ? "alert" : "status");
}

function getCurrentReturnPath(): string {
  const path = `${window.location.pathname || "/"}${window.location.search || ""}${window.location.hash || ""}`;
  return path.startsWith("/") ? path : "/";
}

function canUseControls(): boolean {
  return state.session === "active";
}

function controlsDisabledAttr(): string {
  return canUseControls() ? "" : "disabled";
}

function transportDisabledAttr(track: { trackId: string }): string {
  if (!canUseControls()) return "disabled";
  if (state.playState === "accepting" || state.playState === "queued") return "disabled";
  if (!track.trackId) return "disabled";
  return "";
}

function feedbackDisabledAttr(track: { trackId: string }): string {
  return canUseControls() && track.trackId ? "" : "disabled";
}

function stopPolling() {
  if (pollTimer !== null) {
    window.clearInterval(pollTimer);
    pollTimer = null;
  }
}

function startAcceptWait() {
  [
    { delayMs: 1_500, message: "pidiendo musica" },
    { delayMs: 6_000, message: "Music esta tardando" },
    { delayMs: 12_000, message: "sigo esperando Music" },
  ].forEach(({ delayMs, message }) => {
    const timer = window.setTimeout(() => {
      if (state.playState !== "accepting") return;
      state.statusMessage = message;
      render();
    }, delayMs);
    acceptWaitTimers.push(timer);
  });
}

function stopAcceptWait() {
  acceptWaitTimers.forEach((timer) => window.clearTimeout(timer));
  acceptWaitTimers = [];
}

async function loadSpeakerSnapshot() {
  if (!canUseControls()) return;
  state.speakerState = "loading";
  state.speakerMessage = "leyendo bocinas";
  render();
  const result = await getPlayerState();
  if (!result.ok) {
    if (result.status === 401) {
      await enterSessionRequired(result.message);
      return;
    }
    state.speakerState = "soft_failed";
    state.speakerMessage = friendlyError(result.message);
    render();
    return;
  }
  applyPlayerSnapshot(result.data);
  state.speakerState = "ready";
  state.speakerMessage = speakerOutputs().some((output) => isHouseOutput(output))
    ? "salidas listas"
    : "sin HomePods; Mac-only listo";
  render();
}

async function chooseOutputs(outputNames: string[]) {
  if (!canUseControls()) return;
  state.speakerState = "saving";
  state.speakerMessage = outputNames.length ? "cambiando bocinas" : "limpiando AirPlay";
  render();
  const result = await setCasaSonoraOutputs(outputNames);
  if (!result.ok) {
    if (result.status === 401) {
      await enterSessionRequired(result.message);
      return;
    }
    state.speakerState = "soft_failed";
    state.speakerMessage = friendlyError(result.message);
    render();
    return;
  }
  applyOutputResult(result.data);
  state.speakerState = "ready";
  state.speakerMessage = outputNames.length ? result.data.message || "ruta actualizada" : "Solo Mac activo";
  render();
}

async function applyVolume() {
  if (!canUseControls()) return;
  const nextVolume = clampUiVolume(state.volumeValue);
  state.volumeValue = nextVolume;
  state.volumeState = "saving";
  state.volumeMessage = "aplicando guard";
  render();
  const result = await setCasaSonoraVolume({
    outputNames: state.selectedOutputNames,
    volume: nextVolume,
  });
  if (!result.ok) {
    if (result.status === 401) {
      await enterSessionRequired(result.message);
      return;
    }
    state.volumeState = "failed";
    state.volumeMessage = friendlyError(result.message);
    render();
    return;
  }
  applyVolumeResult(result.data);
  state.volumeState = "saved";
  state.volumeMessage = result.data.message || `volumen en ${state.volumeValue}`;
  render();
}

async function loadHealthPanel() {
  if (!canUseControls()) return;
  state.healthState = "loading";
  state.auditState = "loading";
  state.healthMessage = "consultando bridge";
  state.auditMessage = "consultando audit";
  render();

  const [health, audit] = await Promise.all([getCasaSonoraHealth(), getAuditRecent(12)]);
  if (!health.ok) {
    if (health.status === 401) {
      await enterSessionRequired(health.message);
      return;
    }
    state.health = null;
    state.healthState = classifyBridgeDown(health.message) ? "down" : "warn";
    state.healthMessage = health.error === "rhythm_health_failed" ? "bridge apagado o no alcanzable" : friendlyError(health.message);
  } else {
    state.health = health.data;
    state.healthState = state.warmup === "ready" ? "ready" : "warn";
    state.healthMessage = "bridge responde";
  }

  if (!audit.ok) {
    if (audit.status === 401) {
      await enterSessionRequired(audit.message);
      return;
    }
    state.auditEvents = [];
    state.auditState = "failed";
    state.auditMessage = friendlyError(audit.message);
    if (classifyBridgeDown(audit.message) && state.healthState !== "down") {
      state.healthState = "warn";
    }
  } else {
    state.auditEvents = Array.isArray(audit.data.events) ? audit.data.events.slice().reverse() : [];
    state.auditState = "ready";
    state.auditMessage = audit.data.redacted ? "audit redactado" : "audit recibido";
    if (recentAuditHasFailure() && state.healthState === "ready") {
      state.healthState = "warn";
    }
  }
  render();
}

async function refreshPlayerState(reason: "job_complete" | "control") {
  if (state.session !== "active" || playerRefreshInFlight) return;
  playerRefreshInFlight = true;
  const result = await getPlayerState();
  playerRefreshInFlight = false;

  if (!result.ok) {
    if (result.status === 401) {
      await enterSessionRequired(result.message);
    }
    return;
  }

  applyPlayerSnapshot(result.data);
  render();
}

function applyPlayerSnapshot(payload: PlayerStatusPayload) {
  const nested = typeof payload.playerState === "object" && payload.playerState !== null ? payload.playerState : null;
  const playerState = normalizePlayerState(typeof payload.playerState === "string" ? payload.playerState : nested?.playerState);
  const currentTrack = payload.currentTrack || nested?.currentTrack || null;
  if (playerState !== "unknown") {
    state.playerState = playerState;
  }
  if (currentTrack) {
    state.currentTrack = currentTrack;
  }
  const outputs = outputLabelFromPayload(payload, nested);
  if (outputs) {
    state.outputsLabel = outputs;
  }
  applyOutputSnapshot(payload);
  if (state.playState !== "accepting" && state.playState !== "queued" && state.playState !== "failed") {
    state.playState = state.playerState === "playing" ? "playing" : "idle";
    state.statusMessage = state.playerState === "playing" ? "sonando" : "pausado";
  }
}

function normalizeTrack(track: TrackRef | null): {
  trackId: string;
  name: string;
  artist: string;
  album: string;
  initials: string;
  artworkUrl: string;
  sourcePlaylists: string[];
} {
  const name = track?.name || track?.title || "Casa Sonora";
  const artist = track?.artist || track?.artistName || "elige un mood";
  const album = track?.album || "";
  const artworkUrl = track?.artworkUrl || track?.artwork?.url || "";
  const trackId = track?.trackId || track?.persistentId || track?.persistentID || track?.id || "";
  return {
    trackId,
    name,
    artist,
    album,
    artworkUrl,
    initials: name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((word) => word[0]?.toUpperCase())
      .join("") || "CS",
    sourcePlaylists: Array.isArray(track?.sourcePlaylists) ? track.sourcePlaylists : [],
  };
}

function trackMeta(track: { artist: string; album: string }): string {
  return track.album ? `${track.artist} · ${track.album}` : track.artist;
}

function trackLabel(track: { trackId: string }): string {
  if (!track.trackId) return "esperando";
  if (state.playerState === "paused") return "pausado";
  return "sonando";
}

function transportButtonLabel(track: { trackId: string }, action: "play" | "pause"): string {
  if (!canUseControls()) return state.session === "checking" ? "Abriendo" : "Espera";
  if (state.playState === "accepting" || state.playState === "queued") return "Preparando";
  if (!track.trackId) return "Elige un mood";
  return action === "pause" ? "Pausar" : "Reproducir";
}

function outputLabel(payload: CasaBootstrap): string {
  const names = [
    ...(Array.isArray(payload.selectedOutputNames) ? payload.selectedOutputNames : []),
    ...(Array.isArray(payload.playerState?.selectedOutputNames) ? payload.playerState.selectedOutputNames : []),
    ...(Array.isArray(payload.outputNames) ? payload.outputNames : []),
  ].filter(Boolean);
  if (!names.length) {
    return "Solo Mac";
  }
  return names.slice(0, 2).join(", ");
}

function outputLabelFromPayload(payload: PlayerStatusPayload, nested: PlayerStatusPayload | null): string {
  const names = [
    ...(Array.isArray(payload.selectedOutputNames) ? payload.selectedOutputNames : []),
    ...(Array.isArray(nested?.selectedOutputNames) ? nested.selectedOutputNames : []),
    ...(Array.isArray(payload.outputNames) ? payload.outputNames : []),
    ...(Array.isArray(nested?.outputNames) ? nested.outputNames : []),
  ].filter(Boolean);
  return names.length ? names.slice(0, 2).join(", ") : "";
}

function applyOutputSnapshot(payload: CasaBootstrap | PlayerStatusPayload) {
  const nested = typeof (payload as PlayerStatusPayload).playerState === "object" ? ((payload as PlayerStatusPayload).playerState as PlayerStatusPayload) : null;
  const devices = extractOutputDevices(payload, nested);
  const selectedNames = extractSelectedOutputNames(payload, nested);
  if (devices.length) {
    state.outputDevices = devices;
  }
  if (selectedNames.length || devices.length) {
    state.selectedOutputNames = selectedNames;
    state.outputsLabel = selectedNames.length ? selectedNames.slice(0, 2).join(", ") : "Solo Mac";
  }
  const maxVolume = maxVolumeFromPayload(payload);
  if (maxVolume !== null) {
    state.volumeMax = maxVolume;
    state.volumeValue = Math.min(state.volumeValue, maxVolume);
  }
}

function applyOutputResult(payload: OutputsResult) {
  const devices = extractOutputDevices(payload, null);
  if (devices.length) {
    state.outputDevices = devices;
  }
  const selectedNames = extractSelectedOutputNames(payload, null);
  state.selectedOutputNames = selectedNames;
  state.outputsLabel = selectedNames.length ? selectedNames.slice(0, 2).join(", ") : "Solo Mac";
  const maxVolume = maxVolumeFromPayload(payload);
  if (maxVolume !== null) {
    state.volumeMax = maxVolume;
    state.volumeValue = Math.min(state.volumeValue, maxVolume);
  }
}

function applyVolumeResult(payload: VolumesResult) {
  const devices = extractOutputDevices(payload, null);
  if (devices.length) {
    state.outputDevices = devices;
  }
  const selectedNames = extractSelectedOutputNames(payload, null);
  if (selectedNames.length || devices.length) {
    state.selectedOutputNames = selectedNames;
    state.outputsLabel = selectedNames.length ? selectedNames.slice(0, 2).join(", ") : "Solo Mac";
  }
  const maxVolume = maxVolumeFromPayload(payload);
  if (maxVolume !== null) {
    state.volumeMax = maxVolume;
  }
  const nextVolume = Number.isFinite(payload.volume) ? Number(payload.volume) : Number(payload.volumeGuard?.volume);
  if (Number.isFinite(nextVolume)) {
    state.volumeValue = clampUiVolume(nextVolume);
  }
}

function extractOutputDevices(payload: unknown, nested: unknown): CasaOutput[] {
  const candidates = [
    (payload as { audioOutputs?: unknown[] })?.audioOutputs,
    (payload as { outputs?: unknown[] })?.outputs,
    (payload as { selectedOutputs?: unknown[] })?.selectedOutputs,
    (nested as { audioOutputs?: unknown[] })?.audioOutputs,
    (nested as { outputs?: unknown[] })?.outputs,
    (nested as { selectedOutputs?: unknown[] })?.selectedOutputs,
  ];
  for (const candidate of candidates) {
    if (!Array.isArray(candidate) || !candidate.length) continue;
    return candidate
      .map((item) => normalizeOutputDevice(item))
      .filter((item): item is CasaOutput => Boolean(item?.name));
  }
  return [];
}

function normalizeOutputDevice(item: unknown): CasaOutput | null {
  if (typeof item === "string") {
    const name = item.trim();
    return name ? { name, supportsAudio: true } : null;
  }
  if (!item || typeof item !== "object") return null;
  const raw = item as Record<string, unknown>;
  const name = String(raw.name || raw.outputName || raw.id || "").trim();
  if (!name) return null;
  return {
    name,
    kind: String(raw.kind || raw.type || "").trim(),
    available: toBoolean(raw.available, true),
    active: toBoolean(raw.active, false),
    selected: toBoolean(raw.selected, false),
    supportsAudio: toBoolean(raw.supportsAudio, true),
    soundVolume: numericOrUndefined(raw.soundVolume ?? raw.volume),
    volume: numericOrUndefined(raw.volume ?? raw.soundVolume),
  };
}

function extractSelectedOutputNames(payload: unknown, nested: unknown): string[] {
  const names = [
    ...stringList((payload as { selectedOutputNames?: unknown })?.selectedOutputNames),
    ...stringList((payload as { outputNames?: unknown })?.outputNames),
    ...stringList((payload as { requestedOutputNames?: unknown })?.requestedOutputNames),
    ...stringList((nested as { selectedOutputNames?: unknown })?.selectedOutputNames),
    ...stringList((nested as { outputNames?: unknown })?.outputNames),
  ];
  const devices = extractOutputDevices(payload, nested);
  devices.forEach((device) => {
    if ((device.selected || device.active) && device.name && isHouseOutput(device)) {
      names.push(device.name);
    }
  });
  return uniqueStrings(names);
}

function maxVolumeFromPayload(payload: unknown): number | null {
  const guard = (payload as { volumeGuard?: { maxVolume?: unknown } })?.volumeGuard;
  const max = numericOrUndefined(guard?.maxVolume);
  return Number.isFinite(max) ? Math.max(1, Math.min(35, Math.round(Number(max)))) : null;
}

function speakerOutputs(): CasaOutput[] {
  return state.outputDevices.filter((output) => output.name);
}

function outputNamesFromDevices(outputs: CasaOutput[]): string[] {
  return outputs.filter((output) => (output.selected || output.active) && isHouseOutput(output)).map((output) => String(output.name));
}

function isHouseOutput(output: CasaOutput): boolean {
  const kind = String(output.kind || "").toLowerCase();
  if (kind.includes("computer")) return false;
  return output.supportsAudio !== false;
}

function speakerMeta(output: CasaOutput, house: boolean, available: boolean, volume: number | null): string {
  if (!house) return "Mac / sistema";
  if (!available) return "no disponible";
  const parts = [String(output.kind || "AirPlay").trim() || "AirPlay"];
  if (volume !== null) parts.push(`vol ${Math.min(volume, state.volumeMax)}`);
  if (volume !== null && volume > state.volumeMax) parts.push("guard recorta");
  return parts.join(" · ");
}

function clampUiVolume(value: number): number {
  if (!Number.isFinite(value)) return state.volumeMax;
  return Math.max(0, Math.min(state.volumeMax, Math.round(value)));
}

function stringList(value: unknown): string[] {
  return Array.isArray(value) ? value.map((item) => String(item || "").trim()).filter(Boolean) : [];
}

function uniqueStrings(values: string[]): string[] {
  return Array.from(new Set(values.map((value) => value.trim()).filter(Boolean)));
}

function toBoolean(value: unknown, fallback: boolean): boolean {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    if (/^(true|yes|1)$/i.test(value)) return true;
    if (/^(false|no|0)$/i.test(value)) return false;
  }
  return fallback;
}

function numericOrUndefined(value: unknown): number | undefined {
  const next = Number(value);
  return Number.isFinite(next) ? next : undefined;
}

function viewTitle(view: ViewKey): string {
  if (view === "bocinas") return "Bocinas";
  if (view === "salud") return "Salud";
  return "Ahora";
}

function statusLabel(): string {
  if (state.view === "salud") {
    const health = healthSummary();
    return health.title;
  }
  if (state.view === "bocinas") {
    if (state.speakerState === "saving") return "ajustando ruta";
    if (state.volumeState === "saving") return "guard activo";
    return state.selectedOutputNames.length ? "AirPlay listo" : "Mac listo";
  }
  return state.statusMessage;
}

function statusToneForView(): "good" | "watch" | "bad" {
  if (state.view === "salud") {
    const health = healthSummary();
    return health.tone === "bad" ? "bad" : health.tone === "watch" ? "watch" : "good";
  }
  if (state.view === "bocinas") {
    if (state.speakerState === "soft_failed" || state.volumeState === "failed") return "bad";
    return state.selectedOutputNames.length ? "watch" : "good";
  }
  return state.playState === "failed" && state.session === "active" ? "bad" : state.warmup === "ready" ? "good" : "watch";
}

function healthSummary(): { tone: "good" | "watch" | "bad"; title: string; detail: string; bridgeLabel: string; actionTitle: string; actionTone: string; action: string } {
  if (state.healthState === "down") {
    return {
      tone: "bad",
      title: "bridge apagado",
      detail: state.healthMessage || "No pude alcanzar el home-bridge.",
      bridgeLabel: "sin respuesta",
      actionTitle: "reactivar bridge",
      actionTone: "runbook recovery",
      action: "Abrir el runbook Casa Sonora recovery: primero verificar 4311 como airstride; luego 4312 como Adrian si Music debe operar. Si 4312 fue apagado a propósito, dejarlo así y no intentar play.",
    };
  }
  if (state.healthState === "warn" || recentAuditHasFailure()) {
    return {
      tone: "watch",
      title: "ruta con aviso",
      detail: state.healthMessage || "El bridge responde, pero hay una señal reciente que revisar.",
      bridgeLabel: "responde",
      actionTitle: "revisar reciente",
      actionTone: "sin audio",
      action: "Revisar auditoría reciente y confirmar que la ruta siga Mac-only. Si aparece 502 o timeout repetido, empaquetar el hallazgo antes de tocar bridges.",
    };
  }
  if (state.healthState === "loading") {
    return {
      tone: "watch",
      title: "consultando",
      detail: "Leyendo health y audit reciente.",
      bridgeLabel: "consultando",
      actionTitle: "esperar lectura",
      actionTone: "sin audio",
      action: "Casa Sonora está consultando estado sin disparar reproducción.",
    };
  }
  return {
    tone: "good",
    title: "sano",
    detail: state.healthMessage === "sin consultar" ? "Toca actualizar salud para leer health y audit." : "Bridge responde y no hay fallos recientes visibles.",
    bridgeLabel: "listo",
    actionTitle: "seguir",
    actionTone: "Mac-only",
    action: "Mantener Mac-only y usar la pantalla Ahora. Si algo falla, volver aquí antes de abrir recovery.",
  };
}

function recentAuditHasFailure(): boolean {
  return state.auditEvents.some((event) => {
    const text = `${event.event || ""} ${event.result || ""} ${event.error || ""} ${event.message || ""}`.toLowerCase();
    return /failure|failed|timeout|502|error|fallo/.test(text);
  });
}

function classifyBridgeDown(message: string): boolean {
  return /502|fetch failed|home-bridge|bridge|no pude consultar|no pude alcanzar|apagado/i.test(message);
}

function lastAuditLabel(): string {
  const event = state.auditEvents[0];
  if (!event) return state.auditMessage || "sin audit";
  const action = [event.kind, event.action, event.result || event.event].filter(Boolean).join(" / ");
  return action || "evento reciente";
}

function formatShortTime(value: string): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value.slice(0, 18);
  return new Intl.DateTimeFormat("es-MX", { hour: "2-digit", minute: "2-digit", second: "2-digit" }).format(date);
}

function redactAuditText(value: string): string {
  return value
    .replace(/Bearer\s+[A-Za-z0-9._~+/-]+/gi, "Bearer [redactado]")
    .replace(/([?&](?:token|key|secret|password)=)[^&\s]+/gi, "$1[redactado]")
    .replace(/[A-Za-z0-9_-]{32,}/g, "[redactado]")
    .slice(0, 120);
}

function normalizePlayerState(value: unknown): AppState["playerState"] {
  const normalized = String(value || "").toLowerCase();
  if (normalized.includes("play")) return "playing";
  if (normalized.includes("pause") || normalized.includes("stop")) return "paused";
  return "unknown";
}

function normalizePhase(value: unknown): AppState["activePhase"] {
  const phase = String(value || "").trim() as PhaseKey;
  return phase in phaseCopy ? phase : "queue_pick";
}

function phaseStatus(phase: PhaseKey): "done" | "active" | "idle" | "failed" {
  if (state.activePhase === "failed") return "failed";
  if (phase === state.activePhase) return "active";
  const order = Object.keys(phaseCopy) as PhaseKey[];
  if (order.indexOf(phase) < order.indexOf(state.activePhase as PhaseKey)) return "done";
  return "idle";
}

function phaseTiming(phase: PhaseKey): string {
  const keyByPhase: Record<PhaseKey, string[]> = {
    queue_pick: ["queue_pick_ms"],
    track_lookup: ["track_lookup_ms"],
    applescript_spawn: ["applescript_spawn_ms"],
    music_confirm: ["music_confirm_ms"],
    playing: ["home_bridge_total_ms", "total_ms"],
  };
  const value = keyByPhase[phase].map((key) => state.phaseTimings[key]).find((item) => Number.isFinite(item));
  return Number.isFinite(value) ? `${Math.round(Number(value))} ms` : "";
}

function warmupLabel(): string {
  if (state.warmup === "ready") return "lista";
  if (state.warmup === "running") return "calentando";
  if (state.warmup === "soft_failed") return "Mac-only";
  return state.warmupMessage;
}

function resultLine(activeMood?: Mood): string {
  if (state.session === "checking") return "conectando con Music";
  if (state.booting) return "abriendo Casa Sonora";
  if (state.warmup === "running") return "preparando ruta Mac-only";
  if (state.playState === "accepting") return state.statusMessage === "tap recibido" ? "enviando decision a Music" : state.statusMessage;
  if (state.playState === "queued") return `job ${state.activeJobId}`;
  if (state.feedbackState === "saving") return "guardando feedback";
  if (state.feedbackState === "saved" || state.feedbackState === "failed") return state.feedbackMessage || "feedback guardado";
  if (state.playState === "playing") return activeMood ? `${activeMood.label} en curso` : "sonando";
  if (state.lastTapAckMs !== null) return `respuesta visual ${state.lastTapAckMs} ms`;
  return "Mac-only";
}

function friendlyError(message: string): string {
  if (/osascript timed out/i.test(message)) {
    return "Music no respondio en 12 s. Pausa o cierra Music y vuelve a intentar.";
  }
  if (/fetch failed/i.test(message)) {
    return "No pude llegar al bridge de Casa Sonora.";
  }
  return message;
}

function feedbackResultMessage(kind: "favorite" | "disliked", payload: FeedbackResult): string {
  if (kind === "favorite" && payload.favoriteUpdated) {
    return "favorito confirmado en Music";
  }
  if (kind === "disliked" && payload.dislikedUpdated) {
    return "no me gusta confirmado en Music";
  }
  if (payload.ratingError) {
    return "feedback guardado; Music no confirmó rating";
  }
  if (payload.message) {
    return payload.message;
  }
  return kind === "favorite"
    ? "feedback favorito guardado; Music no devolvió confirmación de favorito"
    : "feedback no me gusta guardado; Music no devolvió confirmación de rating";
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (char) => {
    const replacements: Record<string, string> = {
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#39;",
    };
    return replacements[char] || char;
  });
}

function escapeAttribute(value: string): string {
  return escapeHtml(value).replace(/\)/g, "%29").replace(/\(/g, "%28");
}

function playIcon() {
  return `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5.6v12.8c0 .8.9 1.3 1.6.9l9.6-6.4a1.1 1.1 0 0 0 0-1.8L9.6 4.7C8.9 4.3 8 4.8 8 5.6Z" fill="currentColor"/></svg>`;
}

function pauseIcon() {
  return `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5h2.8c.6 0 1 .4 1 1v12c0 .6-.4 1-1 1H8c-.6 0-1-.4-1-1V6c0-.6.4-1 1-1Zm5.2 0H16c.6 0 1 .4 1 1v12c0 .6-.4 1-1 1h-2.8c-.6 0-1-.4-1-1V6c0-.6.4-1 1-1Z" fill="currentColor"/></svg>`;
}

function heartIcon() {
  return `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 20.2 4.9 13C2.7 10.8 3 7.1 5.5 5.3c2-1.4 4.7-.9 6.1 1.1l.4.6.4-.6c1.4-2 4.1-2.5 6.1-1.1 2.5 1.8 2.8 5.5.6 7.7L12 20.2Z" fill="currentColor"/></svg>`;
}

function thumbDownIcon() {
  return `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8.2 3h8.5c1.2 0 2.2.8 2.5 1.9l1.2 4.4c.4 1.6-.8 3.2-2.5 3.2h-3.7l.7 3.5c.2.8-.1 1.6-.7 2.1l-1.7 1.6c-.5.5-1.4.2-1.5-.5l-1.3-6.7H6.1c-.9 0-1.6-.7-1.6-1.6V6.7c0-.5.2-.9.5-1.2L6.7 3.7c.4-.4.9-.7 1.5-.7Z" fill="currentColor"/></svg>`;
}

void boot();
